import axios from 'axios'
import { requestServer } from "$lib/servers.svelte";
import { invalidateServerToken, getServerToken } from "$lib/auth.svelte";
import { AxiosError } from "axios";

export async function readPage(
  server: any,
  seriesId: string,
  title: string,
  num: string,
  subNum: string,
  page: number,
  opts?: { translate?: string; force?: boolean },
) {
  try {
    let query =
      `api/v1/series/${seriesId}/read?vol=${title == 'volume' ? num : 0}&chp=${
        title == 'chapter' ? num : 0}&svol=${title == 'volume' ? subNum : 0}&schp=${
          title == 'chapter' ? subNum : 0}&num=${page}`
    if (opts?.translate && opts.translate !== 'disabled') {
      query += `&translate=${encodeURIComponent(opts.translate)}`
      if (opts.force) query += '&force=true'
    }
    const { data, status } = await requestServer(
      server,
      'get',
      query,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if ((error as AxiosError)?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return {}
  }
}

export async function readPageByBook(
  server: any,
  bookId: string,
  page: number,
  opts?: { translate?: string; force?: boolean },
) {
  try {
    let query = `api/v1/read/${bookId}?num=${page}`
    if (opts?.translate && opts.translate !== 'disabled') {
      query += `&translate=${encodeURIComponent(opts.translate)}`
      if (opts.force) query += '&force=true'
    }
    const { data, status } = await requestServer(
      server,
      'get',
      query,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if ((error as AxiosError)?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return {}
  }
}

/** Fetch a translated manga page as a Blob (MANGA/WEBTOON only). */
export async function readTranslatedPage(
  server: any,
  bookId: string,
  page: number,
  force = false,
  signal?: AbortSignal,
): Promise<Blob | null> {
  const token = await getServerToken(server.uid)
  if (!token) {
    invalidateServerToken(server.uid)
    return null
  }

  const urls: string[] = server?.urls ?? []
  if (!urls.length) return null

  const query = force
    ? `api/v1/read/${bookId}?num=${page}&translate=ENG&force=true`
    : `api/v1/read/${bookId}?num=${page}&translate=ENG`

  let lastErr: unknown
  for (const baseURL of urls) {
    try {
      const res = await axios({
        method: 'get',
        url: `${baseURL}/${query}`,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        signal,
        timeout: 6 * 60 * 1000,
      })
      if (res.status === 401) {
        invalidateServerToken(server.uid)
        return null
      }
      if (res.status >= 400) {
        throw new Error('translation failed')
      }
      return res.data instanceof Blob ? res.data : new Blob([res.data])
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 401) {
        invalidateServerToken(server.uid)
        return null
      }
      if ((error as Error)?.name === 'CanceledError' || (error as Error)?.name === 'AbortError') {
        throw error
      }
      lastErr = error
    }
  }
  console.log(lastErr)
  return null
}

/** Fetch a translated novel page as text (NOVEL/WEBNOVEL). Original remains available via plain read. */
export async function readTranslatedNovelPage(
  server: any,
  bookId: string,
  page: number,
  force = false,
  signal?: AbortSignal,
  seriesOpts?: {
    seriesId: string
    type: string
    num: string
    subNum: string
  },
): Promise<string | null> {
  const token = await getServerToken(server.uid)
  if (!token) {
    invalidateServerToken(server.uid)
    return null
  }

  const urls: string[] = server?.urls ?? []
  if (!urls.length) return null

  let path: string
  if (bookId) {
    path = force
      ? `api/v1/read/${bookId}?num=${page}&translate=ENG&force=true`
      : `api/v1/read/${bookId}?num=${page}&translate=ENG`
  } else if (seriesOpts) {
    const { seriesId, type, num, subNum } = seriesOpts
    path =
      `api/v1/series/${seriesId}/read?vol=${type == 'volume' ? num : 0}&chp=${
        type == 'chapter' ? num : 0}&svol=${type == 'volume' ? subNum : 0}&schp=${
          type == 'chapter' ? subNum : 0}&num=${page}&translate=ENG`
    if (force) path += '&force=true'
  } else {
    return null
  }

  let lastErr: unknown
  for (const baseURL of urls) {
    try {
      const res = await axios({
        method: 'get',
        url: `${baseURL}/${path}`,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text',
        signal,
        timeout: 6 * 60 * 1000,
        transformResponse: [(data) => data],
      })
      if (res.status === 401) {
        invalidateServerToken(server.uid)
        return null
      }
      if (res.status >= 400) {
        throw new Error('translation failed')
      }
      const text = typeof res.data === 'string' ? res.data : String(res.data ?? '')
      return text
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 401) {
        invalidateServerToken(server.uid)
        return null
      }
      if ((error as Error)?.name === 'CanceledError' || (error as Error)?.name === 'AbortError') {
        throw error
      }
      lastErr = error
    }
  }
  console.log(lastErr)
  return null
}

/** Fetch an enhanced manga page as a Blob (MANGA/WEBTOON only). Original remains available via plain read URL. */
export async function readEnhancedPage(
  server: any,
  bookId: string,
  page: number,
  force = false,
  signal?: AbortSignal,
): Promise<Blob | null> {
  const urls: string[] = server?.urls ?? []
  if (!urls.length) return null

  const query = force
    ? `api/v1/read/${bookId}?num=${page}&enhance=true&force=true`
    : `api/v1/read/${bookId}?num=${page}&enhance=true`

  let lastErr: unknown
  for (const baseURL of urls) {
    try {
      const res = await axios({
        method: 'get',
        url: `${baseURL}/${query}`,
        responseType: 'blob',
        signal,
        timeout: 6 * 60 * 1000,
      })
      if (res.status >= 400) {
        throw new Error('enhance failed')
      }
      const ct = String(res.headers?.['content-type'] ?? '')
      // Enhanced responses are JPEG; resolution-skip returns original as octet-stream.
      if (!ct.includes('image/jpeg') && res.headers?.['x-nuread-enhanced'] !== '1') {
        return null
      }
      return res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'image/jpeg' })
    } catch (error) {
      if ((error as Error)?.name === 'CanceledError' || (error as Error)?.name === 'AbortError') {
        throw error
      }
      lastErr = error
    }
  }
  console.log(lastErr)
  return null
}

export async function bookProgress(server: any, seriesId: string, bookId: string ) {
  try {
    const { data, status } = await requestServer(
      server,
      'get',
      `api/v1/progress/${seriesId}/book/${bookId}`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if ((error as AxiosError)?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return {}
  }
}

export async function listBooks(server: any, seriesId: string) {
  try {
    const { data, status } = await requestServer(
      server,
      'get',
      `api/v1/series/${seriesId}/books`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if ((error as AxiosError)?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return {}
  }
}

export async function updateProgress(server: any, seriesId: string, bookId: string, num: number, offset = 0) {
  try {
    const clamped = Math.min(1, Math.max(0, offset))
    const { data, status } = await requestServer(
      server,
      'post',
      `api/v1/progress/${seriesId}/book/${bookId}?page=${num}&offset=${clamped}`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if ((error as AxiosError)?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return {}
  }
}

export async function markCompleted(server: any, seriesId: string, bookId: string) {
  try {
    const { data, status } = await requestServer(
      server,
      'patch',
      `api/v1/progress/${seriesId}/book/${bookId}/completed?complete=true`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if ((error as AxiosError)?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return {}
  }
}

export async function markUncompleted(server: any, seriesId: string, bookId: string) {
  try {
    const { data, status } = await requestServer(
      server,
      'patch',
      `api/v1/progress/${seriesId}/book/${bookId}/completed?complete=false`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if ((error as AxiosError)?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return {}
  }
}
